'use client';

import React, { CSSProperties, forwardRef, SyntheticEvent } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { usePathname, useSearchParams } from '@framework/next/navigation';
import { UrlObject } from 'url';
import styles from './nextLink.module.scss';

export interface INextLinkProps {
  title?: string,
  dataTest?: string,
  className?: string,
  isActive?: boolean,
  isExactActive?: boolean,
  activeClassName?: string,
  to: string | UrlObject,
  target?: string,
  rel?: string,
  children?: React.ReactNode,
  onClick?: (e: any) => void,
  onMouseEnter?: (e: any) => void,
  onMouseLeave?: (e: any) => void,
  onTouchStart?: (e: SyntheticEvent) => void,
  onFocus?: (e: SyntheticEvent) => void,
  onBlur?: (e: SyntheticEvent) => void,
  as?: string,
  ariaCurrent?: 'page',
  disabled?: boolean,
  passHref?: boolean,
  shallow?: boolean,
  id?: string,
  locale?: string,
  prefetch?: boolean,
  stopPropagation?: boolean,
  style?: CSSProperties,
  tabIndex?: number,
}

const NextLink = forwardRef<HTMLAnchorElement, INextLinkProps>(({
  dataTest,
  title,
  className,
  to,
  children,
  activeClassName = '',
  isActive,
  isExactActive,
  as,
  ariaCurrent,
  onClick,
  disabled,
  passHref,
  shallow,
  onMouseEnter,
  target,
  id,
  prefetch,
  stopPropagation = false,
  ...other
}, ref) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isPrefetchUseless = disabled || target === '_blank';
  const resolvedPrefetch = prefetch ?? (isPrefetchUseless ? false : undefined);

  const asPath = searchParams.toString() ? `${ pathname }?${ searchParams.toString() }` : pathname;
  const toPathname = typeof to === 'string' ? to : to.pathname;

  // @ts-ignore
  const isLinkActive = isActive || (isExactActive ? asPath === toPathname : asPath.includes(toPathname));

  const onHandleClick = (e: SyntheticEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (stopPropagation) {
      e.stopPropagation();
    }

    onClick?.(e);
  };

  return (
    <Link
      href={ to }
      as={ as }
      passHref={ passHref }
      shallow={ shallow }
      onClick={ onHandleClick }
      onMouseEnter={ onMouseEnter }
      target={ target }
      prefetch={ resolvedPrefetch }
      aria-disabled={ disabled }
      title={ title }
      data-test={ dataTest }
      aria-current={ ariaCurrent }
      className={ cn(className, {
        [activeClassName]: isLinkActive,
        [styles.isDisabled]: disabled,
       }) }
      tabIndex={ disabled ? -1 : undefined }
      ref={ ref }
      id={ id }
      { ...other }
    >
      { children }
    </Link>
  );
});

export default NextLink;
